import React, { useContext } from 'react';
import {
  MDBContainer,
  MDBRow,
  MDBCol,
  MDBTypography,
  MDBBtn,
  MDBRadio,
  MDBAlert,
  MDBCard,
  MDBCardBody,
  MDBBadge
} from 'mdb-react-ui-kit';
import ReactCountryFlag from 'react-country-flag';
import { useTranslation } from 'react-i18next';
import { useFormData } from '../hooks/useFormData';
import { useStepNavigation } from '../hooks/useStepNavigation';
import { useValidation } from '../components/ValidationProvider';
import { DevContext } from '../../../../../App';
import { LANGUAGES } from '../types/lectureForm.types';

const LANGUAGE_OPTIONS = [
  {
    value: LANGUAGES.DE,
    label: 'Deutsch',
    countryCode: 'DE'
  },
  {
    value: LANGUAGES.EN,
    label: 'English',
    countryCode: 'US'
  }
];

export default function LanguageStep({ onComplete }) {
  const { t } = useTranslation();
  const { formData, handleFieldChange, hasFieldError, getFieldError } = useFormData();
  const { goToNextStep, goToPreviousStep } = useStepNavigation();
  const { getStepValidation, getStepRequiredFields, getFieldValidation } = useValidation();
  const { isDevMode } = useContext(DevContext);

  // Get validation info from schema
  const stepValidation = getStepValidation('language');
  const requiredFields = getStepRequiredFields('language');
  const languageFieldValidation = getFieldValidation('language');

  const handleLanguageChange = (language) => {
    handleFieldChange('language', language);
  };

  const handleContinue = () => {
    if (formData.language && stepValidation.isValid) {
      onComplete?.();
      goToNextStep();
    }
  };

  const canContinue = formData.language && stepValidation.isValid;

  return (
    <div className="d-flex flex-column h-100">
      <MDBContainer className="flex-grow-1 pb-5">
      <MDBTypography tag="h3" className="mb-4">
        {t('Select Lecture Language')}
      </MDBTypography>
      
      <MDBRow className="mb-4">
        <MDBCol>
          <MDBTypography tag="p" className="mb-4 text-muted">
            {t('Please select the language in which your lecture will be held.')}
          </MDBTypography>
          
          {LANGUAGE_OPTIONS.map((option) => (
            <div key={option.value} className="mb-3">
              <MDBRadio
                name="language"
                id={option.value}
                value={option.value}
                label={
                  <div className="d-flex align-items-center">
                    <ReactCountryFlag 
                      countryCode={option.countryCode}
                      svg
                      style={{
                        width: '1.2em',
                        height: '1.2em',
                        marginRight: '0.5rem'
                      }}
                    />
                    <span>{option.label}</span>
                  </div>
                }
                checked={formData.language === option.value}
                onChange={() => handleLanguageChange(option.value)}
              />
            </div>
          ))}
        </MDBCol>
      </MDBRow>

      {hasFieldError('language') && (
        <MDBRow className="mb-3">
          <MDBCol>
            <MDBAlert color="danger">
              {getFieldError('language')}
            </MDBAlert>
          </MDBCol>
        </MDBRow>
      )}

      {/* Validation Debug Info */}
      {isDevMode && (
        <MDBRow className="mb-4">
          <MDBCol>
            <MDBCard className="border-info">
              <MDBCardBody>
                <MDBTypography tag="h6" className="mb-3">
                  🔍 Validation Debug Info (Dev Mode) - Language Step
                </MDBTypography>
              
              <div className="mb-2">
                <strong>Step Required Fields:</strong> {requiredFields.join(', ') || 'none'}
              </div>
              
              <div className="mb-2">
                <strong>Selected Language:</strong>{' '}
                {formData.language ? (
                  <span className="d-inline-flex align-items-center">
                    <ReactCountryFlag 
                      countryCode={LANGUAGE_OPTIONS.find(opt => opt.value === formData.language)?.countryCode}
                      svg
                      style={{
                        width: '1em',
                        height: '1em',
                        marginRight: '0.25rem'
                      }}
                    />
                    {LANGUAGE_OPTIONS.find(opt => opt.value === formData.language)?.label}
                  </span>
                ) : (
                  'None selected'
                )}
              </div>
              
              <div className="mb-2">
                <strong>Local Validation:</strong>{' '}
                <MDBBadge color={formData.language ? 'success' : 'warning'}>
                  {formData.language ? 'Valid (Language selected)' : 'Invalid (No language)'}
                </MDBBadge>
              </div>
              
              <div className="mb-2">
                <strong>Schema Validation:</strong>{' '}
                <MDBBadge color={stepValidation.isValid ? 'success' : 'danger'}>
                  {stepValidation.isValid ? 'Valid' : 'Invalid'}
                </MDBBadge>
              </div>
              
              {stepValidation.errors.length > 0 && (
                <div className="mb-2">
                  <strong>Schema Errors:</strong>
                  <ul className="mb-0">
                    {stepValidation.errors.map((error, index) => (
                      <li key={index}>
                        <code>{error.field}</code>: {error.message}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              
              {languageFieldValidation && (
                <div className="mb-2">
                  <strong>Field-Specific Error:</strong>{' '}
                  <code>{languageFieldValidation.field}</code>: {languageFieldValidation.message}
                </div>
              )}
              
              <div className="mb-2">
                <strong>Continue Button:</strong>{' '}
                <MDBBadge color={canContinue ? 'success' : 'warning'}>
                  {canContinue ? 'Enabled' : 'Disabled'}
                </MDBBadge>
              </div>
            </MDBCardBody>
          </MDBCard>
        </MDBCol>
      </MDBRow>
      )}

      </MDBContainer>
      
      {/* Fixed button container at bottom */}
      <div className="bg-white" style={{ 
        position: 'sticky', 
        bottom: 0,
        padding: '1rem',
        marginTop: 'auto',
        zIndex: 10
      }}>
        <MDBContainer>
          <MDBRow>
            <MDBCol className="d-flex justify-content-center gap-5">
              <MDBBtn 
                color="secondary" 
                onClick={goToPreviousStep}
                outline
              >
                {t('Back')}
              </MDBBtn>
              <MDBBtn 
                onClick={handleContinue}
                disabled={!canContinue}
              >
                {t('Continue')}
              </MDBBtn>
            </MDBCol>
          </MDBRow>
        </MDBContainer>
      </div>
    </div>
  );
}